import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Sparkles, ChevronRight } from 'lucide-react';
import { VOICE_NAV } from '@/data/voiceAgents';
import { cn } from '@/lib/utils';

/**
 * VoiceLayout — frame that wraps every AI Voice Agents sub-page.
 * Renders a sub-navigation rail (horizontal tab bar) under the page eyebrow.
 */
export const VoiceLayout = ({ eyebrow = 'AI Voice Agents', title, subtitle, actions, children }) => {
  const { pathname } = useLocation();
  const current = VOICE_NAV.find((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to))) || VOICE_NAV[0];

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Crumb */}
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Sparkles className="h-3 w-3 text-primary" aria-hidden="true" />
        <span className="uppercase tracking-widest">{eyebrow}</span>
        <ChevronRight className="h-3 w-3" aria-hidden="true" />
        <span className="text-foreground">{current.label}</span>
      </div>

      {/* Title row */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground max-w-3xl">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>

      {/* Sub-nav rail */}
      <nav className="overflow-x-auto scrollbar-thin border-b border-border" aria-label="AI Voice Agents sub-navigation">
        <ul className="flex items-center gap-1 min-w-max">
          {VOICE_NAV.map((n) => (
            <li key={n.to}>
              <NavLink
                to={n.to}
                end={n.end}
                className={({ isActive }) => cn(
                  'inline-block px-3 py-2 text-[12.5px] rounded-t-md transition-colors border-b-2',
                  isActive
                    ? 'border-primary text-foreground font-medium'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/60'
                )}
                data-testid={`voicenav-${n.to.split('/').pop()}`}
              >
                {n.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {children}
    </div>
  );
};

export default VoiceLayout;
